/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { css, cx } from "@panda/css";

import { LogoList } from "./LogoList.molecule";
import { LogoListSectionProps } from "@components/elements/ref-sections";

const defaultLogoHeight = 54;
const gridLogoHeight = 90;
const duration = 120;

/**
 * Renders a list of logos either with an animation or in a grid.
 *
 * **Marquee structure:** the track contains TWO copies of the logo list
 * placed side-by-side via flex (gap: 64px between them, matching the
 * inter-logo gap). The track animates `translateX(0)` → `translateX(-50%)`
 * over the cycle duration, which equals exactly one list's worth of scroll.
 * When the animation loops, the second copy is in the position the first
 * just vacated — seamless infinite scroll, no visual jump, no overlap.
 *
 * **Why not the older "two absolute lists with delay" pattern:** that
 * approach renders both copies at the same origin (position: absolute)
 * with a 50%-of-duration animation delay between them. Sounds equivalent
 * but isn't — both copies are absolutely positioned at the SAME (0,0) in
 * their parent and animated through a 200% range (translateX(100%) →
 * translateX(-100%)). At many points in the cycle, both are partially
 * visible at OVERLAPPING world positions, producing a visible double-up
 * (e.g. on a 17-logo EAC marquee you'd see Apple Arcade and PlayStation 4
 * stacked in the same horizontal slot).
 */
export const LogoMarquee = ({
	logos = [],
	disableMarquee = false,
	className,
}: LogoListSectionProps): React.JSX.Element | null => {
	const [grid, setGrid] = useState<boolean>(false);

	const logoListRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

	// Decide whether one list fits in the viewport (grid/static mode) or
	// needs to scroll (marquee mode). The marquee track itself is sized
	// purely via `width: max-content` — no JS-driven width measurement
	// needs to be threaded into its layout.
	const checkWidth = useCallback(() => {
		if (logoListRef.current) {
			const width = logoListRef.current.getBoundingClientRect().width;
			// 20px buffer for scrollbars
			setGrid(width + 20 < document.body.clientWidth);
		}
	}, []);

	useEffect(() => {
		if (!disableMarquee) {
			checkWidth();
			window.addEventListener("resize", checkWidth);
			return () => window.removeEventListener("resize", checkWidth);
		}
		return () => {};
	}, [disableMarquee, checkWidth]);

	const marquee = !disableMarquee && !grid;

	if (!logos) {
		return null;
	}

	const height = disableMarquee ? gridLogoHeight : defaultLogoHeight;

	// Non-marquee modes (grid + small-screen-fits) — render one list inside
	// a centering flex wrapper. The wrapper lets the inner LogoList shrink
	// to its content width and stay horizontally centered in the section,
	// preserving the pre-marquee-rewrite visual (clustered group of logos
	// centered in the viewport, NOT spread evenly across the full width).
	if (!marquee) {
		return (
			<div className={cx("logo-marquee", css({ height: `${height}px`, w: "full" }), className)}>
				<div className={css({ display: "flex", justifyContent: "center", w: "full" })}>
					<LogoList ref={logoListRef} logos={logos} height={height} marquee={false} gridLayout={disableMarquee} />
				</div>
			</div>
		);
	}

	// Marquee mode — two side-by-side copies in a translated track.
	//
	// We rely on `width: max-content` to size the flex track to EXACTLY
	// 2× one list's content width. `translateX(-50%)` in the keyframe is
	// then exactly one list width — a seamless loop regardless of viewport.
	//
	// Do NOT add `minWidth: 2 * logoListWidth` here. On the first paint
	// (before the useEffect measurement runs) `logoListWidth` is the
	// 2400px default, so any minWidth derived from it can be larger than
	// the actual content. That would force the track wider than its
	// content, making `translateX(-50%)` translate further than one list
	// — which renders as a brief stretch of "double speed" animation
	// until the resize listener corrects state. `max-content` alone keeps
	// box and content in lockstep.
	return (
		<div className={cx("logo-marquee", css({ height: `${height}px`, overflow: "hidden", w: "full" }), className)}>
			<div
				className={cx(
					"logo-marquee-track",
					css({
						display: "flex",
						width: "max-content",
						animationName: "marqueeTrack",
						animationDuration: `${duration}s`,
						animationTimingFunction: "linear",
						animationIterationCount: "infinite",
					}),
				)}
			>
				<LogoList ref={logoListRef} logos={logos} height={defaultLogoHeight} marquee />
				<LogoList logos={logos} height={defaultLogoHeight} marquee aria-hidden />
			</div>
		</div>
	);
};
